import React, { useMemo, useState } from 'react';
import { AGVotingQuestion, Arbeitsgruppe } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface AGVotingPageProps {
  questions: AGVotingQuestion[];
  arbeitsgruppen: Arbeitsgruppe[];
  myArbeitsgruppenIds: number[];
  onCastVote: (questionId: number, arbeitsgruppeId: number) => Promise<void>;
}

const AGVotingPage: React.FC<AGVotingPageProps> = ({
  questions,
  arbeitsgruppen,
  myArbeitsgruppenIds,
  onCastVote,
}) => {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [questionId: number]: string }>({});

  const visibleQuestions = useMemo(
    () => questions.filter((q) => q.isVisible),
    [questions]
  );

  const handleVote = async (questionId: number, arbeitsgruppeId: number) => {
    const key = `${questionId}:${arbeitsgruppeId}`;
    setLoadingKey(key);
    setErrors((prev) => ({ ...prev, [questionId]: '' }));

    try {
      await onCastVote(questionId, arbeitsgruppeId);
    } catch (e: any) {
      setErrors((prev) => ({
        ...prev,
        [questionId]: e?.message || 'Stimme konnte nicht gespeichert werden',
      }));
    } finally {
      setLoadingKey(null);
    }
  };

  if (visibleQuestions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-500">
        Aktuell sind keine Abstimmungen sichtbar.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {visibleQuestions.map((question) => {
        const selectedArbeitsgruppeId = question.currentUserVote?.arbeitsgruppeId || null;
        const counts = question.voteCounts || {};
        const totalVotes = question.showResults
          ? Object.values(counts).reduce((sum, value) => sum + value, 0)
          : 0;
        const questionError = errors[question.id];

        return (
          <div key={question.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-bold text-gray-900">{question.question}</h2>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                  question.isOpen ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {question.isOpen ? 'Offen' : 'Geschlossen'}
              </span>
            </div>

            {questionError && (
              <div className="p-3 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                {questionError}
              </div>
            )}

            <div className="space-y-2">
              {arbeitsgruppen.map((arbeitsgruppe) => {
                const isOwnArbeitsgruppe = myArbeitsgruppenIds.includes(arbeitsgruppe.id);
                const isSelected = selectedArbeitsgruppeId === arbeitsgruppe.id;
                const isLoading = loadingKey === `${question.id}:${arbeitsgruppe.id}`;
                const disabled = isOwnArbeitsgruppe || !question.isOpen || isLoading || loadingKey !== null;
                const count = counts[arbeitsgruppe.id] || 0;
                const percent = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;

                return (
                  <button
                    key={arbeitsgruppe.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleVote(question.id, arbeitsgruppe.id)}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition ${
                      isSelected ? 'border-primary bg-primary/10' : 'border-gray-200 bg-white'
                    } ${!disabled ? 'hover:border-primary/60' : 'opacity-70 cursor-not-allowed'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-900 font-medium">
                        {`Challenge ${arbeitsgruppe.challenge}: ${arbeitsgruppe.name}`}
                      </span>

                      <div className="flex items-center gap-3">
                        {question.showResults && (
                          <span className="text-sm text-gray-600 font-semibold">{count}</span>
                        )}
                        {isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          isSelected && (
                            <span className="material-symbols-outlined text-primary text-base" aria-hidden="true">
                              check_circle
                            </span>
                          )
                        )}
                      </div>
                    </div>

                    {question.showResults && (
                      <div className="mt-2">
                        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                          <div className="h-full bg-primary/70 transition-all" style={{ width: `${percent}%` }} />
                        </div>
                        <div className="mt-1 text-xs text-gray-500 text-right">{percent}%</div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {!question.isOpen && (
              <p className="mt-3 text-sm text-amber-700">Diese Abstimmung ist geschlossen.</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AGVotingPage;
